import { useEffect, useRef, useState } from "react";
import { Loader2, MessageSquareText, RotateCcw, Send, Sparkles, X } from "lucide-react";
import { MiniMarkdown } from "@/components/advisor/MiniMarkdown";
import { useAdvisor } from "@/components/advisor/useAdvisor";

const STARTERS = ["Fire safety for a 6-floor hospital", "Which extinguisher for a restaurant kitchen?", "Evacuation chairs for an office tower", "We have EV parking in the basement"];

const Bubble = ({ m, onNavigate, i }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`advisor-message-${i}`}>
    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "rounded-br-sm bg-[#123634] text-white" : "rounded-bl-sm bg-[#F4F6F2] text-slate-800"}`}>
      {m.role === "user" ? m.content : m.content ? <MiniMarkdown text={m.content} onNavigate={onNavigate} /> : <span className="inline-flex gap-1" data-testid="advisor-typing"><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:120ms]" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:240ms]" /></span>}
    </div>
  </div>
);

const Starters = ({ send }) => (
  <div className="space-y-3" data-testid="advisor-starters">
    <p className="text-sm text-slate-600">Tell me about your building and I'll suggest the right Vinsafe products. Try:</p>
    {STARTERS.map((s, i) => (
      <button key={s} type="button" onClick={() => send(s)} className="block w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-left text-sm text-slate-700 transition-colors hover:border-[#123634]" data-testid={`advisor-starter-${i}`}>{s}</button>
    ))}
  </div>
);

const Panel = ({ onClose }) => {
  const { messages, busy, error, send, reset, load } = useAdvisor();
  const [text, setText] = useState("");
  const end = useRef(null);
  const first = useRef(true);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!messages.length) return;
    end.current?.scrollIntoView({ behavior: first.current ? "auto" : "smooth" });
    first.current = false;
  }, [messages]);
  const submit = (e) => { e.preventDefault(); send(text); setText(""); };
  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-white shadow-2xl sm:inset-auto sm:bottom-24 sm:right-6 sm:h-[600px] sm:max-h-[calc(100vh-120px)] sm:w-[400px] sm:rounded-2xl sm:border sm:border-slate-200" data-testid="advisor-panel">
      <div className="flex items-center gap-3 border-b border-slate-100 bg-[#123634] px-4 py-3 text-white sm:rounded-t-2xl">
        <Sparkles className="h-5 w-5 text-[#E8927F]" />
        <div className="min-w-0 flex-1"><p className="text-sm font-semibold">Vinsafe Product Advisor</p><p className="text-[11px] text-slate-300">AI assistant · confirm details on your quote</p></div>
        <button type="button" onClick={reset} aria-label="New chat" className="rounded-md p-1.5 hover:bg-white/10" data-testid="advisor-new-chat"><RotateCcw className="h-4 w-4" /></button>
        <button type="button" onClick={onClose} aria-label="Close advisor" className="rounded-md p-1.5 hover:bg-white/10" data-testid="advisor-close"><X className="h-5 w-5" /></button>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="advisor-messages">
        {messages.length ? messages.map((m, i) => <Bubble key={i} m={m} i={i} onNavigate={onClose} />) : <Starters send={send} />}
        {error && <p className="rounded-lg bg-[#FBEDEA] px-3 py-2 text-xs text-[#C20500]" data-testid="advisor-error">{error}</p>}
        <div ref={end} />
      </div>
      <form onSubmit={submit} className="flex items-center gap-2 border-t border-slate-100 p-3">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={800} placeholder="Describe your site or ask about a product" className="h-11 min-w-0 flex-1 rounded-xl border border-slate-300 px-3.5 text-sm outline-none focus:border-[#123634]" data-testid="advisor-input" />
        <button type="submit" disabled={busy || !text.trim()} aria-label="Send" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#C20500] text-white transition-opacity disabled:opacity-40" data-testid="advisor-send">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</button>
      </form>
    </div>
  );
};

export const AdvisorWidget = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      {open && <Panel onClose={() => setOpen(false)} />}
      <button type="button" onClick={() => setOpen((o) => !o)} className="fixed bottom-24 right-6 z-50 flex items-center gap-2 rounded-full bg-[#123634] px-4 py-3 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5" data-testid="advisor-open-button">
        {open ? <X className="h-4 w-4" /> : <MessageSquareText className="h-4 w-4 text-[#E8927F]" />}
        <span className="hidden sm:inline">{open ? "Close" : "Ask our product advisor"}</span>
      </button>
    </>
  );
};
